import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { getWards, getAdmissions, type Ward, type AdmissionRow } from '@/lib/ipdApi';
import { Bed as BedIcon, Search, Plus, Settings, Receipt, Printer, ListFilter, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, subDays } from 'date-fns';

const DEFAULT_RANGE_DAYS = 30;
const defaultDateFrom = () => format(subDays(new Date(), DEFAULT_RANGE_DAYS), 'yyyy-MM-dd');
import AdmitPatientModal from '@/components/IPD/AdmitPatientModal';

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    admitted:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    discharged:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    lama:         'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    absconded:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    referred_out: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  };
  const labels: Record<string, string> = {
    referred_out: 'Referred Out',
  };
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize',
      styles[status] ?? 'bg-muted text-muted-foreground',
    )}>
      {labels[status] ?? status}
    </span>
  );
}

// ─── Select helper ─────────────────────────────────────────────────────────────

function Sel({ value, onChange, children, disabled }: {
  value: string; onChange: (v: string) => void;
  children: React.ReactNode; disabled?: boolean;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
      className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50">
      {children}
    </select>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function IPDPage() {
  const { currentUser } = useAuth();
  const { can, isOwner, hasFeature } = usePermission();
  const navigate = useNavigate();
  const canWrite = can('ipd:write');
  const canManageWards = isOwner || currentUser?.role === 'admin';

  const [admissions, setAdmissions] = useState<AdmissionRow[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'admitted' | 'all'>('admitted');
  const [wardFilter, setWardFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'due'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [wards, setWards] = useState<Ward[]>([]);
  const [admitOpen,  setAdmitOpen]  = useState(false);

  // Debounce search — used to decide whether to widen the "All" date range server-side
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // A non-empty search looks for a specific patient/admission across all history,
  // so it overrides the default "last 30 days" window for the "All" view.
  const dateFilterActive = statusFilter === 'all' && !debouncedSearch.trim();

  const load = useCallback(() => {
    if (!currentUser?.clinic_id) return;
    setLoading(true);
    getAdmissions(currentUser.clinic_id, {
      status: statusFilter,
      dateFrom: dateFilterActive ? dateFrom || undefined : undefined,
      dateTo:   dateFilterActive ? dateTo   || undefined : undefined,
    })
      .then(data => setAdmissions(data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentUser?.clinic_id, statusFilter, dateFilterActive, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!currentUser?.clinic_id) return;
    getWards(currentUser.clinic_id).then(w => setWards(w ?? [])).catch(console.error);
  }, [currentUser?.clinic_id]);

  if (!hasFeature('ipd')) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <BedIcon className="w-8 h-8 text-muted-foreground mb-2" />
        <h2 className="text-base font-semibold mb-1">IPD Not Available</h2>
        <p className="text-sm text-muted-foreground max-w-sm">
          In-patient department management is not included in your current plan. Contact support to upgrade.
        </p>
      </div>
    );
  }

  const activeFilterCount =
    (wardFilter ? 1 : 0) +
    (statusFilter === 'all' && paymentFilter === 'due' ? 1 : 0) +
    (statusFilter === 'all' && dateFrom !== defaultDateFrom() ? 1 : 0) +
    (statusFilter === 'all' && dateTo ? 1 : 0);

  const filtered = admissions.filter(a => {
    if (wardFilter && a.ward_id !== wardFilter) return false;
    if (paymentFilter === 'due' && !(Number(a.balance_due) > 0)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      a.patient_name?.toLowerCase().includes(q) ||
      a.patient_mrn?.toLowerCase().includes(q)  ||
      a.admission_number?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b shrink-0">
        <div className="flex items-center gap-2">
          <BedIcon className="w-5 h-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold">IPD</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Status filter */}
          <div className="flex rounded-md border overflow-hidden text-xs">
            {(['admitted', 'all'] as const).map(s => (
              <button
                key={s}
                onClick={() => {
                  setStatusFilter(s);
                  if (s === 'admitted') {
                    setPaymentFilter('all');
                    setDateFrom('');
                    setDateTo('');
                  } else if (s === 'all' && !dateFrom && !dateTo) {
                    setDateFrom(defaultDateFrom());
                  }
                }}
                className={cn(
                  'px-3 py-1.5 capitalize transition-colors',
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted text-muted-foreground',
                )}
              >
                {s === 'admitted' ? 'Current' : 'All'}
              </button>
            ))}
          </div>
          {/* Filters popover: Ward, Payment, Date range */}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs relative">
                <ListFilter className="w-3.5 h-3.5 mr-1" /> Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Ward</label>
                <Sel value={wardFilter} onChange={setWardFilter}>
                  <option value="">All Wards</option>
                  {wards.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </Sel>
              </div>

              {/* Payment + date range — only meaningful once discharged/invoiced admissions are in view */}
              {statusFilter === 'all' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Payment</label>
                    <Sel value={paymentFilter} onChange={v => setPaymentFilter(v as 'all' | 'due')}>
                      <option value="all">All Payments</option>
                      <option value="due">Due Only</option>
                    </Sel>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Admission Date Range</label>
                    <div className="space-y-2">
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        className="h-8 text-sm w-full"
                      />
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        className="h-8 text-sm w-full"
                      />
                    </div>
                  </div>
                </>
              )}

              {activeFilterCount > 0 && (
                <Button
                  size="sm" variant="ghost" className="h-7 w-full text-xs text-muted-foreground"
                  onClick={() => { setWardFilter(''); setPaymentFilter('all'); setDateFrom(statusFilter === 'all' ? defaultDateFrom() : ''); setDateTo(''); }}
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear filters
                </Button>
              )}
            </PopoverContent>
          </Popover>
          {statusFilter === 'all' && !search.trim() && dateFrom && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Showing {dateTo
                ? `${format(new Date(dateFrom), 'dd MMM')} – ${format(new Date(dateTo), 'dd MMM')}`
                : `since ${format(new Date(dateFrom), 'dd MMM yyyy')}`}
            </span>
          )}
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search patient, MRN…"
              className="h-8 pl-8 text-sm w-52"
            />
          </div>
          {/* Wards settings */}
          {canManageWards && (
            <Button size="sm" variant="outline" onClick={() => navigate('/ipd/settings')} title="Wards & Beds">
              <Settings className="w-4 h-4" />
            </Button>
          )}
          {/* Admit button */}
          {canWrite && (
            <Button size="sm" onClick={() => setAdmitOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Admit Patient
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto px-4 py-4">
        {loading ? (
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  {[
                    'Adm. No.', 'Patient', 'Ward / Bed', 'Doctor', 'Admitted',
                    ...(statusFilter === 'all' ? ['Discharged'] : []),
                    'Bill#', 'Total', 'Paid', 'Due', 'Advance', 'Status', '',
                  ].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                    {Array.from({ length: statusFilter === 'all' ? 13 : 12 }).map((_, j) => (
                      <td key={j} className="px-3 py-2.5">
                        <div className="h-4 rounded bg-muted animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">
            {search ? 'No matching admissions.' : 'No admissions found.'}
          </div>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  {[
                    'Adm. No.', 'Patient', 'Ward / Bed', 'Doctor', 'Admitted',
                    ...(statusFilter === 'all' ? ['Discharged'] : []),
                    'Bill#', 'Total', 'Paid', 'Due', 'Advance', 'Status', '',
                  ].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/ipd/admissions/${a.id}`)}
                    className={cn(
                      'border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/50',
                      i % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                    )}
                  >
                    <td className="px-3 py-2.5 font-mono text-xs">{a.admission_number}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium">{a.patient_name}</div>
                      <div className="text-xs text-muted-foreground">{a.patient_mrn}</div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div>{a.ward_name}</div>
                      <div className="text-xs text-muted-foreground">Bed {a.bed_number}</div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs">{a.admitting_doctor_name}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                      {a.admission_date
                        ? format(new Date(a.admission_date), 'dd MMM yyyy')
                        : '—'}
                    </td>
                    {statusFilter === 'all' && (
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">
                        {a.discharge_date
                          ? format(new Date(a.discharge_date), 'dd MMM yyyy')
                          : '—'}
                      </td>
                    )}
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs font-mono">{a.invoice_number || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right">
                      {a.total_amount != null ? `₹${Number(a.total_amount).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right text-green-600">
                      {a.total_amount != null ? `₹${Number(a.paid_amount ?? 0).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className={cn(
                      'px-3 py-2.5 whitespace-nowrap text-right',
                      a.total_amount != null && Number(a.balance_due) > 0 && 'text-red-600 font-medium',
                    )}>
                      {a.total_amount != null ? `₹${Number(a.balance_due ?? 0).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-right text-blue-600">
                      {Number(a.available_advance) > 0 ? `₹${Number(a.available_advance).toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7"
                          title="Billing"
                          onClick={e => { e.stopPropagation(); navigate(`/ipd/admissions/${a.id}?tab=charges`); }}
                        >
                          <Receipt className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7"
                          title="Discharge Summary"
                          onClick={e => { e.stopPropagation(); navigate(`/ipd/admissions/${a.id}?tab=discharge`); }}
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admit Patient Modal */}
      {currentUser?.clinic_id && (
        <AdmitPatientModal
          open={admitOpen}
          onClose={() => setAdmitOpen(false)}
          clinicId={currentUser.clinic_id}
          onAdmitted={id => {
            setAdmitOpen(false);
            navigate(`/ipd/admissions/${id}`);
          }}
        />
      )}
    </div>
  );
}
